import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

/**
 * Seller inventory - what a seller currently has for sale.
 *
 * Distinct from "Sales", which is orders waiting to be dispatched. This screen
 * answers "what am I selling and is it working?"; Sales answers "what do I need
 * to do today?". Conflating the two is why sellers previously had nowhere to
 * see either properly.
 */
interface Listing {
  id: string;
  title: string;
  brand?: string;
  category?: string;
  priceGBP: number;
  imageUrl?: string;
  state: 'DRAFT' | 'PENDING_VERIFICATION' | 'ACTIVE' | 'PAUSED' | 'SOLD' | 'WITHDRAWN' | 'REJECTED';
  views?: number;
  saves?: number;
  createdAt?: string;
}

const STATE_STYLE: Record<string, { label: string; cls: string }> = {
  ACTIVE:               { label: 'Live',            cls: 'bg-ok/15 text-ok-dark' },
  PENDING_VERIFICATION: { label: 'In review',       cls: 'bg-warn/15 text-warn-dark' },
  DRAFT:                { label: 'Draft',           cls: 'bg-warmline text-mute' },
  PAUSED:               { label: 'Paused',          cls: 'bg-warmline text-mute' },
  SOLD:                 { label: 'Sold',            cls: 'bg-gold/15 text-gold-dark' },
  WITHDRAWN:            { label: 'Withdrawn',       cls: 'bg-warmline text-mute' },
  REJECTED:             { label: 'Rejected',        cls: 'bg-bad/10 text-bad' },
};

type Tab = 'live' | 'review' | 'sold';

export default function MyListings() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('live');
  const [editing, setEditing] = useState<Listing | null>(null);
  const [newPrice, setNewPrice] = useState('');

  const { data: listings = [], isLoading } = useQuery<Listing[]>({
    queryKey: ['my-listings'],
    // ?include=sold so the sold tab has history; the tabs filter client-side.
    queryFn: async () => (await api.get('/me/listings', { params: { include: 'sold' } })).data,
  });

  const reprice = useMutation({
    mutationFn: async ({ id, priceGBP }: { id: string; priceGBP: number }) =>
      (await api.patch(`/listings/${id}/price`, { priceGBP })).data,
    onSuccess: () => {
      toast.success('Price updated. Anyone watching this item gets a price-drop alert.');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['my-listings'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update the price.'),
  });

  const buckets = {
    live:   listings.filter((l) => l.state === 'ACTIVE' || l.state === 'PAUSED'),
    review: listings.filter((l) => l.state === 'PENDING_VERIFICATION' || l.state === 'DRAFT' || l.state === 'REJECTED'),
    sold:   listings.filter((l) => l.state === 'SOLD' || l.state === 'WITHDRAWN'),
  };
  const rows = buckets[tab];

  const liveValue = buckets.live.reduce((sum, l) => sum + l.priceGBP, 0);
  const totalViews = buckets.live.reduce((sum, l) => sum + (l.views ?? 0), 0);
  const totalSaves = buckets.live.reduce((sum, l) => sum + (l.saves ?? 0), 0);

  return (
    <div>
      <div className="stamp text-[10px] text-mute">YOUR INVENTORY</div>
      <h1 className="display text-4xl mt-2">My listings</h1>
      <p className="text-mute text-[14px] mt-2 max-w-[60ch]">
        Everything you have for sale. To see orders that need dispatching, go to{' '}
        <Link to="/dashboard/sales" className="text-gold-dark hover:underline">Sales</Link>.
      </p>

      {/* At-a-glance numbers for the live bucket only - draft and sold stock
          would distort them. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {[
          { label: 'Live listings', value: buckets.live.length },
          { label: 'Stock value',   value: `£${liveValue.toFixed(2)}` },
          { label: 'Views',         value: totalViews },
          { label: 'Saves',         value: totalSaves },
        ].map((t) => (
          <div key={t.label} className="card-flat p-4">
            <div className="stamp text-[9px] text-mute">{t.label.toUpperCase()}</div>
            <div className="display text-2xl mt-1">{t.value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-7 border-b border-warmline overflow-x-auto no-scrollbar">
        {([
          { k: 'live',   label: `Live (${buckets.live.length})` },
          { k: 'review', label: `In review (${buckets.review.length})` },
          { k: 'sold',   label: `Sold (${buckets.sold.length})` },
        ] as const).map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2.5 text-[14px] border-b-2 -mb-px transition whitespace-nowrap shrink-0 ${
              tab === t.k ? 'border-gold text-ink font-medium' : 'border-transparent text-mute hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-mute text-sm mt-6">Loading your listings…</p>
      ) : rows.length === 0 ? (
        <div className="card-flat p-10 text-center mt-6">
          <div className="text-3xl mb-3">📦</div>
          <h2 className="display text-xl mb-2">
            {tab === 'live' ? 'Nothing live yet' : tab === 'review' ? 'Nothing in review' : 'Nothing sold yet'}
          </h2>
          <p className="text-mute text-[14px] mb-5 max-w-[42ch] mx-auto">
            {tab === 'live'
              ? 'List your first item - it takes about five minutes, and AI writes the description for you.'
              : tab === 'review'
                ? 'Items appear here while Souqli verifies them, usually within a few hours.'
                : 'Once an item sells it moves here, with the payout recorded in your wallet.'}
          </p>
          {tab === 'live' && <Link to="/dashboard/sell" className="btn btn-primary">Sell an item</Link>}
        </div>
      ) : (
        <div className="space-y-3 mt-6">
          {rows.map((l) => {
            const badge = STATE_STYLE[l.state] ?? STATE_STYLE.DRAFT;
            return (
              <div key={l.id} className="card-flat p-4 flex items-center gap-4">
                <div className="w-16 h-16 rounded-luxe bg-warmline overflow-hidden shrink-0">
                  {l.imageUrl && <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`pill text-[9px] ${badge.cls}`}>{badge.label.toUpperCase()}</span>
                    {l.brand && <span className="stamp text-[9px] text-mute">{l.brand}</span>}
                  </div>
                  <Link to={`/p/${l.id}`} className="block text-[15px] font-medium mt-1 truncate hover:underline">
                    {l.title}
                  </Link>
                  {(l.views != null || l.saves != null) && (
                    <div className="text-[12px] text-mute mt-0.5">
                      {l.views ?? 0} views · {l.saves ?? 0} saves
                      {/* Interest without sales is the signal worth acting on. */}
                      {(l.views ?? 0) >= 25 && (l.saves ?? 0) === 0 && l.state === 'ACTIVE' && (
                        <span className="text-warn-dark"> · lots of views, no saves - try a lower price</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="text-right shrink-0">
                  <div className="display text-xl">£{l.priceGBP.toFixed(2)}</div>
                  {l.state === 'ACTIVE' && (
                    <button
                      onClick={() => { setEditing(l); setNewPrice(String(l.priceGBP)); }}
                      className="text-[12px] text-gold-dark hover:underline mt-1"
                    >
                      Change price
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Repricing modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[380px] max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-xl mb-1">Change price</h3>
            <p className="text-[13px] text-mute mb-4 truncate">{editing.title}</p>

            <label className="stamp text-[10px] text-mute block mb-1.5">NEW PRICE (£)</label>
            <input
              className="input input-lg"
              type="number"
              min="1"
              step="0.01"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              autoFocus
            />

            {Number(newPrice) < editing.priceGBP && Number(newPrice) > 0 && (
              <p className="text-[12px] text-sage-dark mt-2 leading-relaxed">
                That’s a {Math.round((1 - Number(newPrice) / editing.priceGBP) * 100)}% drop. Everyone who saved or
                viewed this item gets a price-drop notification.
              </p>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="btn btn-outline flex-1">Cancel</button>
              <button
                disabled={reprice.isPending || !(Number(newPrice) > 0)}
                onClick={() => reprice.mutate({ id: editing.id, priceGBP: Number(newPrice) })}
                className="btn btn-primary flex-1"
              >
                {reprice.isPending ? 'Saving…' : 'Update price'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
